import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Shield, 
  AlertTriangle, 
  CheckCircle, 
  Trophy, 
  Users,
  Gamepad2,
  Award,
  Mail,
  ChevronDown,
  ChevronUp,
  Flag,
  Ban,
  Scale,
  Heart,
  Zap,
  Target,
  Crown
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Link } from 'react-router-dom';

const FairPlay = () => {
  const [expandedSections, setExpandedSections] = useState<string[]>([]);

  const toggleSection = (section: string) => {
    if (expandedSections.includes(section)) {
      setExpandedSections(expandedSections.filter(s => s !== section));
    } else {
      setExpandedSections([...expandedSections, section]);
    }
  };

  const principles = [
    {
      icon: <Shield className="w-8 h-8 text-[#00fff5]" />,
      title: 'Integrity',
      desc: 'We maintain complete transparency in all tournaments and transactions.'
    },
    {
      icon: <Target className="w-8 h-8 text-[#00fff5]" />,
      title: 'Skill First',
      desc: 'Winners are determined by player performance, not chance or luck.'
    },
    {
      icon: <Heart className="w-8 h-8 text-[#00fff5]" />,
      title: 'Respect',
      desc: 'We foster a positive and respectful gaming community.'
    },
    {
      icon: <Zap className="w-8 h-8 text-[#00fff5]" />,
      title: 'Fair Competition',
      desc: 'Equal opportunities for all players to showcase their skills.'
    }
  ];

  const prohibitedActions = [
    {
      icon: <Ban className="w-5 h-5 text-[#EF4444]" />,
      title: 'Cheating & Hacking',
      desc: 'Using aimbots, wallhacks, modified game clients, or any unauthorized third-party tools'
    },
    {
      icon: <Users className="w-5 h-5 text-[#EF4444]" />,
      title: 'Multiple Accounts',
      desc: 'Creating or using more than one account per user'
    },
    {
      icon: <Flag className="w-5 h-5 text-[#EF4444]" />,
      title: 'Match-Fixing',
      desc: 'Colluding with other players to manipulate match outcomes'
    },
    {
      icon: <AlertTriangle className="w-5 h-5 text-[#EF4444]" />,
      title: 'Abusive Behavior',
      desc: 'Harassment, hate speech, or toxic behavior toward other players'
    },
    {
      icon: <Ban className="w-5 h-5 text-[#EF4444]" />,
      title: 'Account Sharing',
      desc: 'Sharing account credentials or allowing others to play on your behalf'
    },
    {
      icon: <AlertTriangle className="w-5 h-5 text-[#EF4444]" />,
      title: 'Exploiting Bugs',
      desc: 'Intentionally exploiting game glitches or platform vulnerabilities'
    }
  ];

  const penalties = [
    { level: 'First-time minor violation', action: 'Warning', color: '#F59E0B' },
    { level: 'Repeated minor violations', action: 'Temporary Suspension (7-30 days)', color: '#EF4444' },
    { level: 'Cheating, hacking, fraud', action: 'Permanent Ban', color: '#7F1D1D' },
    { level: 'Match-fixing', action: 'Permanent Ban + Legal Action', color: '#7F1D1D' },
  ];

  const faqs = [
    {
      q: 'What happens if I get disconnected during a match?',
      a: 'We understand technical issues can happen. However, individual connection problems are not grounds for refunds or match rescheduling. Ensure stable internet before joining tournaments.'
    },
    {
      q: 'How do you detect cheaters?',
      a: 'We use advanced anti-cheat systems, manual review of suspicious activities, and player reports. Screen recordings may be required for verification in some cases.'
    },
    {
      q: 'Can I appeal a ban?',
      a: 'Yes, you can appeal by emailing support@lastzoneindia.com with details of your case. Appeals are reviewed within 48-72 hours.'
    },
    {
      q: 'What if I see someone cheating?',
      a: 'Report immediately via the in-app report feature or email us with screenshots/videos. All reports are confidential.'
    }
  ];

  return (
    <div className="min-h-screen bg-[#0A0F1E]">
      <Navbar />
      
      {/* Hero Section with Animated Background */}
      <section className="relative pt-32 pb-20 px-4 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-96 h-96 bg-[#00fff5] rounded-full filter blur-3xl opacity-10"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#7000ff] rounded-full filter blur-3xl opacity-10"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] border border-[#00fff5] rounded-full opacity-5 animate-pulse"></div>
        </div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="inline-block p-4 bg-gradient-to-r from-[#00fff5] to-[#7000ff] rounded-2xl mb-6">
              <Shield className="w-12 h-12 text-white" />
            </div>
            <h1 className="text-5xl md:text-7xl font-rajdhani font-bold text-white mb-6">
              Fair Play <span className="text-[#00fff5]">Policy</span>
            </h1>
            <p className="text-xl text-gray-400 font-rajdhani max-w-3xl mx-auto">
              TheArenaX is committed to maintaining a fair, transparent, and competitive environment 
              for all players. Read our commitment to integrity in skill-based gaming.
            </p>
            
            {/* Last Updated Badge */}
            <div className="inline-block mt-8 px-6 py-2 bg-[#1E2338] border border-[#2A2F45] rounded-full">
              <span className="text-gray-400 font-rajdhani">Last Updated: March 2026</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Core Principles */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-rajdhani font-bold text-white mb-4">
              Our Core <span className="text-[#00fff5]">Principles</span>
            </h2>
            <p className="text-xl text-gray-400 font-rajdhani max-w-3xl mx-auto">
              The foundation of every tournament on TheArenaX
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {principles.map((principle, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-gradient-to-b from-[#1E2338] to-[#14182B] border border-[#2A2F45] rounded-2xl p-8 text-center group hover:border-[#00fff5] transition"
              >
                <div className="inline-block p-4 bg-[#00fff510] rounded-xl mb-4 group-hover:bg-[#00fff520] transition">
                  {principle.icon}
                </div>
                <h3 className="text-xl font-rajdhani font-bold text-white mb-3">{principle.title}</h3>
                <p className="text-gray-400 font-rajdhani text-sm">{principle.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Skill-Based Gaming Declaration */}
      <section className="py-20 px-4 bg-[#0A0F1E]">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-block px-4 py-2 bg-[#00fff520] border border-[#00fff5] rounded-full mb-6">
                <span className="text-[#00fff5] font-rajdhani font-bold text-sm">LEGAL COMPLIANCE</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-rajdhani font-bold text-white mb-6">
                Skill-Based <span className="text-[#00fff5]">Gaming</span>
              </h2>
              <p className="text-gray-400 font-rajdhani text-lg mb-6 leading-relaxed">
                TheArenaX hosts only skill-based tournaments where winners are determined by player performance, 
                not chance or luck. This makes us fully compliant with Indian gaming laws and Supreme Court guidelines.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-[#00E676] flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-white font-rajdhani font-bold">Games of Skill</h3>
                    <p className="text-gray-400">BGMI and Free Fire are recognized as games of skill where outcomes depend on player abilities</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-[#00E676] flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-white font-rajdhani font-bold">No Gambling</h3>
                    <p className="text-gray-400">Entry fees are for tournament participation, not betting or wagering</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-[#00E676] flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-white font-rajdhani font-bold">Legal Compliance</h3>
                    <p className="text-gray-400">Fully compliant with all applicable Indian laws and regulations</p>
                  </div>
                </div>
              </div>

              <div className="mt-8 bg-[#00E67620] border border-[#00E676] rounded-xl p-4">
                <p className="text-[#00E676] font-rajdhani font-bold flex items-center gap-2">
                  <Crown className="w-5 h-5" />
                  Verified by Legal Experts
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="bg-gradient-to-r from-[#00fff5] to-[#7000ff] p-1 rounded-3xl">
                <div className="bg-[#0A0F1E] rounded-3xl p-8">
                  <h3 className="text-2xl font-rajdhani font-bold text-white mb-6">Key Factors of Skill</h3>
                  <div className="space-y-4">
                    {[
                      'Aim and accuracy',
                      'Game sense and strategy',
                      'Map knowledge',
                      'Decision making under pressure',
                      'Team coordination',
                      'Reaction time'
                    ].map((item, index) => (
                      <div key={index} className="flex items-center gap-3">
                        <Trophy className="w-5 h-5 text-[#FFD700]" />
                        <span className="text-white font-rajdhani">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Prohibited Actions */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <div className="inline-block p-3 bg-[#EF444420] rounded-xl mb-4">
              <Ban className="w-8 h-8 text-[#EF4444]" />
            </div>
            <h2 className="text-4xl md:text-5xl font-rajdhani font-bold text-white mb-4">
              Prohibited <span className="text-[#EF4444]">Actions</span>
            </h2>
            <p className="text-xl text-gray-400 font-rajdhani max-w-3xl mx-auto">
              Zero tolerance for these violations
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {prohibitedActions.map((action, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-gradient-to-b from-[#1E2338] to-[#14182B] border border-[#2A2F45] rounded-2xl p-6 group hover:border-[#EF4444] transition"
              >
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-[#EF444420] rounded-xl group-hover:bg-[#EF444430] transition">
                    {action.icon}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-white font-rajdhani font-bold mb-2">{action.title}</h3>
                    <p className="text-gray-400 text-sm">{action.desc}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mt-12 bg-[#EF444420] border border-[#EF4444] rounded-2xl p-8 text-center"
          >
            <AlertTriangle className="w-12 h-12 text-[#EF4444] mx-auto mb-4" />
            <h3 className="text-2xl font-rajdhani font-bold text-white mb-4">Zero Tolerance Policy</h3>
            <p className="text-gray-300 max-w-2xl mx-auto">
              Any violation of these rules will result in immediate action, including permanent bans 
              and forfeiture of all winnings. We actively monitor all matches and investigate every report.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Penalties Section */}
      <section className="py-20 px-4 bg-[#0A0F1E]">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <div className="inline-block p-3 bg-[#F59E0B20] rounded-xl mb-4">
              <Scale className="w-8 h-8 text-[#F59E0B]" />
            </div>
            <h2 className="text-4xl md:text-5xl font-rajdhani font-bold text-white mb-4">
              Penalties & <span className="text-[#F59E0B]">Enforcement</span>
            </h2>
          </motion.div>

          <div className="space-y-4">
            {penalties.map((penalty, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-gradient-to-b from-[#1E2338] to-[#14182B] border border-[#2A2F45] rounded-xl p-6"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: penalty.color }}></div>
                    <span className="text-white font-rajdhani">{penalty.level}</span>
                  </div>
                  <span className="font-rajdhani font-bold px-4 py-2 rounded-lg" 
                        style={{ backgroundColor: `${penalty.color}20`, color: penalty.color }}>
                    {penalty.action}
                  </span>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mt-8 bg-[#1E2338] border border-[#2A2F45] rounded-xl p-6"
          >
            <p className="text-gray-400 font-rajdhani text-center">
              <span className="text-[#FFD700] font-bold">Note:</span> Winnings from fraudulent activities 
              will be forfeited and may be redistributed to affected players. All decisions by TheArenaX 
              team are final and made after thorough investigation.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Reporting Section */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-block px-4 py-2 bg-[#00fff520] border border-[#00fff5] rounded-full mb-6">
                <span className="text-[#00fff5] font-rajdhani font-bold text-sm">REPORT MISCONDUCT</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-rajdhani font-bold text-white mb-6">
                See Something? <span className="text-[#00fff5]">Say Something</span>
              </h2>
              <p className="text-gray-400 font-rajdhani text-lg mb-8">
                If you witness any violations of our Fair Play Policy, report it immediately. 
                All reports are confidential and investigated within 24 hours.
              </p>

              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-[#00E676]" />
                  <span className="text-white font-rajdhani">In-app report feature</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-[#00E676]" />
                  <span className="text-white font-rajdhani">Email support with evidence</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-[#00E676]" />
                  <span className="text-white font-rajdhani">Anonymous reporting option</span>
                </div>
              </div>

              <a 
                href="mailto:support@lastzoneindia.com"
                className="inline-flex items-center gap-2 bg-gradient-to-r from-[#00fff5] to-[#7000ff] px-8 py-4 rounded-xl text-white font-rajdhani font-bold hover:opacity-90 transition"
              >
                <Mail className="w-5 h-5" />
                Report to support@lastzoneindia.com
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="bg-gradient-to-b from-[#1E2338] to-[#14182B] border border-[#2A2F45] rounded-2xl p-8"
            >
              <h3 className="text-2xl font-rajdhani font-bold text-white mb-6">What to Include in Your Report</h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-[#00fff510] rounded-lg flex items-center justify-center text-[#00fff5] font-bold flex-shrink-0">1</div>
                  <span className="text-gray-300">Player username(s) involved</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-[#00fff510] rounded-lg flex items-center justify-center text-[#00fff5] font-bold flex-shrink-0">2</div>
                  <span className="text-gray-300">Tournament name and date</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-[#00fff510] rounded-lg flex items-center justify-center text-[#00fff5] font-bold flex-shrink-0">3</div>
                  <span className="text-gray-300">Description of the violation</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-[#00fff510] rounded-lg flex items-center justify-center text-[#00fff5] font-bold flex-shrink-0">4</div>
                  <span className="text-gray-300">Screenshots or video evidence</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-[#00fff510] rounded-lg flex items-center justify-center text-[#00fff5] font-bold flex-shrink-0">5</div>
                  <span className="text-gray-300">Match ID (if available)</span>
                </li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4 bg-[#0A0F1E]">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-rajdhani font-bold text-white mb-4">
              Frequently Asked <span className="text-[#00fff5]">Questions</span>
            </h2>
          </motion.div>

          <div className="space-y-4">
            {faqs.map((faq, index) => {
              const sectionId = `faq-${index}`;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-[#1E2338] border border-[#2A2F45] rounded-xl overflow-hidden"
                >
                  <button
                    onClick={() => toggleSection(sectionId)}
                    className="w-full p-6 flex items-center justify-between hover:bg-[#2A2F45] transition"
                  >
                    <span className="text-white font-rajdhani font-bold text-left">{faq.q}</span>
                    {expandedSections.includes(sectionId) ? (
                      <ChevronUp className="w-5 h-5 text-[#00fff5]" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                  </button>
                  
                  {expandedSections.includes(sectionId) && (
                    <div className="px-6 pb-6">
                      <div className="h-px bg-[#2A2F45] mb-4"></div>
                      <p className="text-gray-400 font-rajdhani">{faq.a}</p>
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Commitment Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-gradient-to-r from-[#00fff510] to-[#7000ff10] border border-[#2A2F45] rounded-3xl p-12 text-center"
          >
            <Award className="w-16 h-16 text-[#00fff5] mx-auto mb-6" />
            <h2 className="text-4xl md:text-5xl font-rajdhani font-bold text-white mb-4">
              Our <span className="text-[#00fff5]">Commitment</span>
            </h2>
            <p className="text-xl text-gray-400 font-rajdhani mb-8 max-w-2xl mx-auto">
              By using TheArenaX, you agree to uphold these fair play principles. Together, 
              we're building India's most trusted skill-based gaming community.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/download">
                <button className="bg-gradient-to-r from-[#00fff5] to-[#7000ff] px-8 py-4 rounded-xl text-white font-rajdhani font-bold hover:opacity-90 transition">
                  Join The Arena
                </button>
              </Link>
              <Link to="/guidelines">
                <button className="bg-[#1E2338] border border-[#2A2F45] px-8 py-4 rounded-xl text-white font-rajdhani font-bold hover:bg-[#2A2F45] transition">
                  Read Community Guidelines
                </button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FairPlay;